import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, Mail, Lock, Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProfilePage({ user, onProfileUpdate }) {
  const [formData, setFormData] = useState({
    name: user.name,
    email: user.email,
    homecell_name: user.homecell_name || "",
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate passwords if changing
    if (formData.new_password) {
      if (formData.new_password !== formData.confirm_password) {
        toast.error("New passwords don't match");
        setLoading(false);
        return;
      }
      if (!formData.current_password) {
        toast.error("Current password is required to set new password");
        setLoading(false);
        return;
      }
    }

    try {
      const token = localStorage.getItem("token");
      const updateData = {
        name: formData.name,
        email: formData.email,
        homecell_name: formData.homecell_name
      };

      if (formData.new_password) {
        updateData.current_password = formData.current_password;
        updateData.new_password = formData.new_password;
      }

      const response = await axios.put(`${API}/profile`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Profile updated successfully");
      onProfileUpdate(response.data);
      
      // Clear password fields
      setFormData({
        ...formData,
        current_password: "",
        new_password: "",
        confirm_password: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          <span className="gradient-text">Profile Settings</span>
        </h1>
        <p className="text-gray-600">Manage your account information</p>
      </div>

      <div className="max-w-2xl">
        <div className="card mb-6">
          <div className="flex items-center gap-4 mb-6 pb-6 border-b">
            <div className="h-20 w-20 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center">
              <User className="h-10 w-10 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{user.name}</h2>
              <p className="text-gray-600">{user.email}</p>
              <span className="inline-block mt-2 px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">
                {user.role === 'super_admin' ? 'Super Admin' : user.role === 'homecell_leader' ? 'Homecell Leader' : 'Disciple'}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    data-testid="profile-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    data-testid="profile-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="[email protected]"
                  />
                </div>

                {(user.role === 'homecell_leader' || user.role === 'super_admin') && (
                  <div>
                    <Label htmlFor="homecell_name">Homecell Name</Label>
                    <Input
                      id="homecell_name"
                      data-testid="profile-homecell-name"
                      value={formData.homecell_name || ''}
                      onChange={(e) => setFormData({ ...formData, homecell_name: e.target.value })}
                      placeholder="e.g., Victory Homecell, Faith Group, etc."
                    />
                    <p className="text-xs text-gray-500 mt-1">Give your homecell a meaningful name</p>
                  </div>
                )}
              </div>
            </div>

            <div className="pt-6 border-t">
              <h3 className="text-lg font-semibold mb-4">Change Password</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="current_password">Current Password</Label>
                  <Input
                    id="current_password"
                    data-testid="profile-current-password"
                    type="password"
                    value={formData.current_password}
                    onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                    placeholder="Enter current password"
                  />
                </div>

                <div>
                  <Label htmlFor="new_password">New Password</Label>
                  <Input
                    id="new_password"
                    data-testid="profile-new-password"
                    type="password"
                    value={formData.new_password}
                    onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                    placeholder="Enter new password"
                  />
                </div>

                <div>
                  <Label htmlFor="confirm_password">Confirm New Password</Label>
                  <Input
                    id="confirm_password"
                    data-testid="profile-confirm-password"
                    type="password"
                    value={formData.confirm_password}
                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                    placeholder="Confirm new password"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-6">
              <Button 
                data-testid="save-profile-button"
                type="submit" 
                disabled={loading} 
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              >
                <Save className="mr-2 h-4 w-4" />
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
